import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Users, Landmark } from "lucide-react";
import { LineReveal, Reveal } from "@/components/Reveal";
import { CredibilityMarquee } from "@/components/Marquee";
import { getToolkits } from "@/lib/api";

const AUDIENCES = [
  { icon: Users, key: "small-business", label: "Small Business", copy: "Plain-language compliance and light-touch support, without a full-time HR hire.", to: "/toolkits" },
  { icon: ShieldCheck, key: "hr-manager", label: "HR Manager", copy: "Audit rigor, wage studies, and policy toolkits to extend your team.", to: "/pay-equity" },
  { icon: Landmark, key: "public-sector", label: "Public Sector", copy: "Public-sector leadership and comparator-based studies built for procurement.", to: "/wage-studies" },
];

const MANIFESTO = [
  { n: "01", t: "Oregon is different.", d: "ORS 652.220 reaches further than the federal Equal Pay Act. Comparable character, not identical titles. Most employers are exposed and don't know it yet." },
  { n: "02", t: "The audit is the fix.", d: "A good-faith equal-pay analysis isn't just paperwork, it's your safe harbor. Do it right and you cap your exposure while you correct course." },
  { n: "03", t: "Judgment, on retainer.", d: "Fractional HR leadership means 27 years of experience making the call, without the cost of a full-time director. Steady guidance, not guesswork." },
];

const Home = () => {
  const [toolkits, setToolkits] = useState([]);

  useEffect(() => {
    getToolkits().then((d) => setToolkits(d.filter((t) => t.featured).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden grain">
        <div className="absolute inset-0 z-0">
          <img src="/generated/hero.png" alt="Cascade mountains at golden hour" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 z-10 bg-ink/60" />
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-ink via-ink/30 to-ink/50" />

        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="text-xs uppercase tracking-[0.3em] text-golden-500 font-semibold mb-6"
          >
            Cascade HR Advisors &middot; Oregon
          </motion.p>
          <h1 className="font-serif text-white leading-[0.92] text-5xl sm:text-7xl lg:text-[8.5rem] tracking-tight">
            <LineReveal lines={["The Oregon", "Pay Equity Fixer."]} delay={0.35} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
            className="text-cream/90 text-lg md:text-xl max-w-xl mt-8 leading-relaxed"
          >
            27 years of HR judgment, grounded in Oregon. Compliance that holds up under ORS 652.220, and fractional HR leadership when you need it most.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9 }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <a href="/pay-equity" data-testid="hero-cta-payequity" className="group flex items-center justify-center gap-2 bg-golden-500 text-navy font-semibold px-8 py-4 rounded-sm transition-colors duration-300 hover:bg-golden-600">
              Fix my pay equity <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a href="/fractional-hr" data-testid="hero-cta-fractional" className="group flex items-center justify-center gap-2 border border-cream/40 text-cream font-semibold px-8 py-4 rounded-md transition-colors duration-300 hover:border-orange hover:text-orange backdrop-blur-sm">
              Explore Fractional HR <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>
        </div>
      </section>

      <CredibilityMarquee />

      {/* AUDIENCE SELF-SELECT */}
      <section data-testid="audience-select" className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-golden-500 font-semibold mb-4">Start where you are</p>
          <h2 className="font-serif text-4xl md:text-6xl text-sage-100 max-w-2xl leading-tight">Who's asking for help today?</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.key} delay={i * 0.1}>
              <a href={a.to} data-testid={`audience-${a.key}`} className="group block h-full bg-forest-900 border border-forest-700 rounded-sm p-8 transition-all duration-300 hover:border-golden-500 hover:-translate-y-1">
                <a.icon className="h-8 w-8 text-golden-500 mb-8" />
                <h3 className="font-serif text-3xl text-sage-100 mb-3">{a.label}</h3>
                <p className="text-sage-300 leading-relaxed mb-8">{a.copy}</p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-golden-500">
                  See how <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section data-testid="manifesto" className="bg-forest-800/70 border-y border-forest-700 py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><p className="text-xs uppercase tracking-[0.3em] text-golden-500 font-semibold mb-16">The Cascade approach</p></Reveal>
          <div className="space-y-4">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.05}>
                <div className="grid md:grid-cols-12 gap-6 items-start border-t border-forest-700 pt-10 pb-10">
                  <div className="md:col-span-4">
                    <span className="font-serif text-[18vw] md:text-[8rem] leading-none text-golden-500/25">{m.n}</span>
                  </div>
                  <div className="md:col-span-7 md:col-start-6">
                    <h3 className="font-serif text-3xl md:text-5xl text-sage-100 mb-4">{m.t}</h3>
                    <p className="text-sage-300 text-lg leading-relaxed max-w-xl">{m.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLKITS TEASER */}
      <section data-testid="toolkits-teaser" className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-golden-500 font-semibold mb-4">Self-serve</p>
            <h2 className="font-serif text-4xl md:text-6xl text-sage-100 max-w-xl leading-tight">Toolkits you can use today.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <a href="/toolkits" data-testid="teaser-all-toolkits" className="group inline-flex items-center gap-2 text-golden-500 font-semibold">
              Browse all toolkits <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {toolkits.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.1}>
              <a href={`/toolkits/${t.id}`} data-testid={`teaser-toolkit-${t.id}`} className="group block bg-forest-900 border border-forest-700 rounded-sm overflow-hidden transition-all duration-300 hover:border-golden-500 hover:-translate-y-1">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-sage-100 mb-2 leading-snug">{t.name}</h3>
                  <p className="text-sage-400 text-sm mb-4">{t.tagline}</p>
                  <span className="text-golden-500 font-semibold text-sm">{t.starting_at}</span>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-32 grain">
        <img src="/generated/contact.png" onError={(e) => (e.currentTarget.src = "/generated/fractional.png")} alt="Alpine lake" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-7xl text-white leading-tight mb-6">Let's fix it before it becomes a problem.</h2>
            <p className="text-cream/90 text-lg max-w-xl mx-auto mb-10">Book a free discovery call. We'll talk through your risk, your team, and the fastest path forward.</p>
            <a href="/contact" data-testid="final-cta" className="group inline-flex items-center gap-2 bg-golden-500 text-navy font-semibold px-10 py-4 rounded-sm transition-colors duration-300 hover:bg-golden-600">
              Book a discovery call <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
